import { UIIcon } from '../../../config/icons';
import { $el } from '../elements';
import { UIView } from '../view';

export interface UITreeAdapter<T> {
    getRoot(): T;
    hasChildren(node: T): boolean;
    getChildren(node: T): T[];
    getLabel(node: T): string;
    getAutoExpand?(node: T): boolean;
    isSelected?(node: T): boolean;
    hasSelected?(node: T): boolean;
    select?(node: T): void;
    // getIcon(node: T): UIIcon;
}

interface TreeItem<T> {
    node: T;
    elem: Element;
    label: Element;
    arrow?: Element;
    expanded: boolean;
    selected: boolean;
    has_selected: boolean;
}

function makeItem<T>(node: T, adapter: UITreeAdapter<T>, depth: number, expanded_root?: boolean): TreeItem<T> {
    const arrow = expanded_root ? undefined : $el.DIV('r-arrow', $el.ICON(UIIcon.ANGLE_RIGHT));
    const label = $el.DIV('r-label t-ellipsize', adapter.getLabel(node));
    const elem = $el.DIV(['ui-tree-item', expanded_root && 'm-no-arrow'], [
        arrow,
        label,
    ]);
    elem.style.setProperty('--ui-tree-depth', depth.toString());
    if(adapter.hasChildren(node))
        elem.classList.add('f-children');

    const expanded = !!adapter.getAutoExpand?.(node);
    const has_selected = !!adapter.hasSelected?.(node);
    const selected = !!adapter.isSelected?.(node);

    elem.classList.toggle('f-selected', selected);
    elem.classList.toggle('f-expanded', expanded || selected || has_selected);

    return { node, elem, arrow, label, expanded, selected, has_selected };
}

function updateItem<T>(item: TreeItem<T>, node: T, adapter: UITreeAdapter<T>) {
    item.selected = !!adapter.isSelected?.(node);
    item.has_selected = !!adapter.hasSelected?.(node);
    item.label.textContent = adapter.getLabel(node);
    item.elem.classList.toggle('f-children', adapter.hasChildren(node));
    item.elem.classList.toggle('f-selected', item.selected);
    item.elem.classList.toggle('f-expanded', item.expanded || item.selected || item.has_selected);
}

interface UITreeOptions {
    expand_root?: boolean;
}

export class UITree<T extends object> extends UIView {
    private _node_map: WeakMap<T, TreeItem<T>>;
    private _elem_map: WeakMap<Element, TreeItem<T>>;
    private _expand_root?: boolean;

    constructor(private _adapter: UITreeAdapter<T>, options: UITreeOptions = {}) {
        super('div', ['ui-tree']);

        this._expand_root = options.expand_root;
        this._node_map = new WeakMap;
        this._elem_map = new WeakMap;
        this._render();

        this.root.addEventListener('click', e => {
            const target = e.target as Element;
            const elem = target.closest('.ui-tree-item');
            if(!elem) return;
            const item = this._elem_map.get(elem);
            if(item) {
                if(item.arrow?.contains(target)) {
                    item.expanded = !item.expanded;
                    this._render();
                    elem.classList.toggle('f-expanded', item.expanded || item.selected || item.has_selected);
                } else {
                    this._adapter.select?.(item.node);
                }
            }
        });
    }

    private _build(node: T, list: TreeItem<T>[], depth: number) {
        const adapter = this._adapter;
        let item = this._node_map.get(node);
        if(item) {
            updateItem(item, node, adapter);
        } else {
            if(depth == 0) {
                item = makeItem(node, adapter, 0, this._expand_root);
            } else {
                item = makeItem(node, adapter, this._expand_root ? depth - 1 : depth);
            }
        }

        list.push(item);

        if(item.expanded || item.selected || item.has_selected) {
            for(const child of adapter.getChildren(node)) {
                this._build(child, list, depth + 1);
            }
        }
    }

    private _render() {
        const list: TreeItem<T>[] = [];
        this._build(this._adapter.getRoot(), list, 0);
        const node_map = new WeakMap<T, TreeItem<T>>();
        const elem_map = new WeakMap<Element, TreeItem<T>>();
        for(const item of list) {
            node_map.set(item.node, item);
            elem_map.set(item.elem, item);
        }

        this._node_map = node_map;
        this._elem_map = elem_map;
        this.ui_render(list.map(item => item.elem));
    }

    update(node?: T) {
        if(node) {
            const item = this._node_map.get(node);
            item && updateItem(item, node, this._adapter);
        } else {
            this._render();
        }
    }
}
